/**
 * TreatmentPlanBuilder.jsx — Phase 2 of TREATMENT-JOURNEY-IMPLEMENTATION-002
 *
 * Provider Admin-only. Lives INSIDE Treatment Detail as the "Treatment Plan"
 * tab. Builds and persists the default operational blueprint for a treatment.
 *
 * Persistence:
 *   GET /api/clinic/treatments/{slug}/plan-template
 *   PUT /api/clinic/treatments/{slug}/plan-template
 *
 * No materialization. No case generation. No assignment. Template only.
 *
 * Uses shared journey-ux primitives:
 *   <DataGrid> · <DetailDrawer> · <DrawerField> · <Pill> · <FilterChips>
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  Archive,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Pencil,
  Trash2,
  CheckCircle2,
} from 'lucide-react';

import { api } from '../../../utils/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import {
  DataGrid,
  DetailDrawer,
  DrawerField,
  FilterChips,
  Pill,
} from '../../../components/journey-ux';

const ROLE_OPTIONS = [
  'Sales',
  'Coordinator',
  'Doctor',
  'After Sales',
  'Accountant',
  'Patient',
];

const KIND_LABEL = { requirement: 'Requirement', task: 'Task' };
const KIND_TONE = { requirement: 'sky', task: 'violet' };
const STATUS_TONE = {
  draft: 'slate',
  active: 'emerald',
  archived: 'stone',
};

function uuid() {
  return `id_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

// Locale keys under provider.planBuilder.*; ROLE_OPTIONS + KIND_LABEL kept as backend enum values.
export default function TreatmentPlanBuilder({ treatment }) {
  const { t } = useTranslation();
  const slug = treatment?.treatment_slug;
  const treatmentName =
    treatment?.custom_name || treatment?.treatment_name || slug || '—';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Source of truth — loaded from / saved to backend.
  const [journeySteps, setJourneySteps] = useState([]); // [{id,name,order,archived}]
  const [rows, setRows] = useState([]); // [{id,step_id,kind,name,patient_action,default_role,default_assignee,document_required,consent_required,due_offset_days,status,notes}]
  const [version, setVersion] = useState(0);
  const [dirty, setDirty] = useState(false);

  // UI state.
  const [stepFilterId, setStepFilterId] = useState('all'); // 'all' or a step.id
  const [activeRowId, setActiveRowId] = useState(null);
  const [renamingStepId, setRenamingStepId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [newStepDraft, setNewStepDraft] = useState('');

  // REQUIREMENT-FOUNDATION-001 Phase 4 — load the Requirement Catalog so
  // builder rows can be backed by a canonical requirement_id rather than
  // a free-text name. Soft-fail if the catalog is empty.
  const [reqCatalog, setReqCatalog] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/clinic/requirements');
        if (data?.success) setReqCatalog(data.items || []);
      } catch (_e) {
        // Non-fatal — the builder still works without a catalog binding.
      }
    })();
  }, []);

  /* ─── Load ─────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/clinic/treatments/${slug}/plan-template`);
      const tpl = data?.plan_template;
      setJourneySteps(Array.isArray(tpl?.journey_steps) ? tpl.journey_steps : []);
      setRows(Array.isArray(tpl?.rows) ? tpl.rows : []);
      setVersion(tpl?.version ?? 0);
      setDirty(false);
    } catch (e) {
      const detail = e?.response?.data;
      setError(detail?.error || detail?.message || e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  /* ─── Save ─────────────────────────────────────────────────── */
  const save = useCallback(async () => {
    if (!slug) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.put(`/clinic/treatments/${slug}/plan-template`, {
        journey_steps: journeySteps,
        rows,
      });
      const tpl = data?.plan_template;
      setVersion(tpl?.version ?? version + 1);
      setDirty(false);
      toast.success(`Treatment Plan saved · v${tpl?.version ?? version + 1}`);
    } catch (e) {
      const detail = e?.response?.data;
      const msg = detail?.error || detail?.message || e.message || 'Save failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [slug, journeySteps, rows, version]);

  /* ─── Journey Step ops ─────────────────────────────────────── */
  const addJourneyStep = (name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    const maxOrder = journeySteps.reduce(
      (acc, s) => (s.order > acc ? s.order : acc),
      -1,
    );
    setJourneySteps([
      ...journeySteps,
      { id: uuid(), name: clean, order: maxOrder + 1, archived: false },
    ]);
    setDirty(true);
    setNewStepDraft('');
  };

  const renameJourneyStep = (id, name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    setJourneySteps(
      journeySteps.map((s) => (s.id === id ? { ...s, name: clean } : s)),
    );
    setDirty(true);
    setRenamingStepId(null);
    setRenameDraft('');
  };

  const moveJourneyStep = (id, direction) => {
    const sorted = [...journeySteps].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapIdx];
    setJourneySteps(
      journeySteps.map((s) => {
        if (s.id === a.id) return { ...s, order: b.order };
        if (s.id === b.id) return { ...s, order: a.order };
        return s;
      }),
    );
    setDirty(true);
  };

  const archiveJourneyStep = (id) => {
    setJourneySteps(
      journeySteps.map((s) => (s.id === id ? { ...s, archived: !s.archived } : s)),
    );
    setDirty(true);
  };

  /* ─── Row ops ──────────────────────────────────────────────── */
  const addRow = (kind, stepId) => {
    if (!stepId) {
      toast.error('Add a Journey Step first');
      return;
    }
    const newRow = {
      id: uuid(),
      step_id: stepId,
      kind,
      name: kind === 'requirement' ? 'New Requirement' : 'New Task',
      patient_action: '',
      default_role: '',
      default_assignee: '',
      document_required: false,
      consent_required: false,
      due_offset_days: null,
      status: 'draft',
      notes: '',
    };
    setRows([...rows, newRow]);
    setActiveRowId(newRow.id);
    setDirty(true);
  };

  const updateRow = (id, patch) => {
    setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const deleteRow = (id) => {
    setRows(rows.filter((r) => r.id !== id));
    if (activeRowId === id) setActiveRowId(null);
    setDirty(true);
  };

  /* ─── Derived ──────────────────────────────────────────────── */
  const sortedSteps = useMemo(
    () => [...journeySteps].sort((a, b) => a.order - b.order),
    [journeySteps],
  );
  const stepMap = useMemo(
    () => Object.fromEntries(sortedSteps.map((s) => [s.id, s])),
    [sortedSteps],
  );
  const filteredRows = useMemo(() => {
    const base =
      stepFilterId === 'all'
        ? rows
        : rows.filter((r) => r.step_id === stepFilterId);
    // Sort by step order, then by name.
    return [...base].sort((a, b) => {
      const oa = stepMap[a.step_id]?.order ?? 999;
      const ob = stepMap[b.step_id]?.order ?? 999;
      if (oa !== ob) return oa - ob;
      return a.name.localeCompare(b.name);
    });
  }, [rows, stepFilterId, stepMap]);

  const activeRow = activeRowId ? rows.find((r) => r.id === activeRowId) : null;

  /* ─── Grid columns ─────────────────────────────────────────── */
  const columns = useMemo(
    () => [
      {
        id: 'step',
        header: 'Journey Step',
        width: 'w-44',
        accessor: (row) => stepMap[row.step_id]?.name || '—',
        cellClassName: 'font-medium text-slate-700',
      },
      {
        id: 'type',
        header: 'Type',
        width: 'w-24',
        render: (row) => (
          <Pill tone={KIND_TONE[row.kind]}>{KIND_LABEL[row.kind]}</Pill>
        ),
      },
      {
        id: 'name',
        header: t('provider.planBuilder.assignedHeading'),
        accessor: (row) => row.name || '—',
        cellClassName: 'font-medium text-slate-900',
      },
      {
        id: 'patient_action',
        header: t('provider.planBuilder.reqOwnerCoordinator') + ' — Patient Action',
        accessor: (row) =>
          row.patient_action ? (
            <span className="text-slate-700">{row.patient_action}</span>
          ) : (
            <span className="text-slate-300">—</span>
          ),
      },
      {
        id: 'default_role',
        header: t('provider.planBuilder.reqOwnerLabel'),
        width: 'w-32',
        accessor: (row) =>
          row.default_role || <span className="text-slate-300">—</span>,
      },
      {
        id: 'default_assignee',
        header: t('provider.planBuilder.reqOwnerClinic'),
        width: 'w-32',
        accessor: (row) =>
          row.default_assignee ? (
            row.default_assignee
          ) : (
            <span className="text-slate-300">—</span>
          ),
      },
      {
        id: 'doc',
        header: t('provider.reqCatalog.typeDocument'),
        width: 'w-20',
        align: 'center',
        render: (row) =>
          row.document_required ? (
            <Pill tone="amber">{t('provider.planBuilder.reqMandatoryLabel')}</Pill>
          ) : (
            <span className="text-slate-300">—</span>
          ),
      },
      {
        id: 'consent',
        header: t('provider.reqCatalog.typeConsent'),
        width: 'w-20',
        align: 'center',
        render: (row) =>
          row.consent_required ? (
            <Pill tone="amber">required</Pill>
          ) : (
            <span className="text-slate-300">—</span>
          ),
      },
      {
        id: 'due',
        header: t('provider.planBuilder.reqOffsetDaysLabel'),
        width: 'w-20',
        align: 'right',
        accessor: (row) =>
          row.due_offset_days != null ? (
            <span className="font-mono text-slate-700">
              {row.due_offset_days >= 0 ? `+${row.due_offset_days}` : row.due_offset_days}d
            </span>
          ) : (
            <span className="text-slate-300">—</span>
          ),
      },
      {
        id: 'status',
        header: t('provider.reqCatalog.fieldType'),
        width: 'w-24',
        render: (row) => <Pill tone={STATUS_TONE[row.status]}>{row.status}</Pill>,
      },
    ],
    [stepMap],
  );

  /* ─── Filter chips: All + each Journey Step ─────────────────── */
  const filterChips = useMemo(() => {
    const all = [
      { id: 'all', label: t('provider.reqCatalog.chipAll', { count: rows.length }), count: rows.length },
      ...sortedSteps.map((s) => ({
        id: s.id,
        label: s.archived ? `${s.name} (${t('provider.reqCatalog.archivedPill')})` : s.name,
        count: rows.filter((r) => r.step_id === s.id).length,
      })),
    ];
    return all;
  }, [sortedSteps, rows]);

  /* ─── Render ───────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="py-10 text-center text-slate-500 text-sm" data-testid="plan-builder-loading">
        <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin text-slate-400" />
        {t('provider.planBuilder.title')}…
      </div>
    );
  }

  return (
    <div data-testid="treatment-plan-builder" className="flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
        <div className="text-[11px] text-slate-500">
          {t('provider.planBuilder.title')} · <span className="font-semibold text-slate-800">{treatmentName}</span> · <span className="font-mono text-slate-500">{slug}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {dirty && (
            <span
              className="text-[11px] text-amber-700 font-semibold"
              data-testid="plan-builder-dirty-flag"
            >
              {t('provider.planBuilder.unsavedChanges')}
            </span>
          )}
          <span
            className="text-[10px] text-slate-400 font-mono"
            data-testid="plan-builder-version"
          >
            v{version}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading || saving}
            data-testid="plan-builder-reload"
            className="h-7 text-[11px]"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            {t('provider.planBuilder.reload')}
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="plan-builder-save"
            className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800 text-white"
          >
            {saving ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Save className="h-3 w-3 mr-1" />
            )}
            {saving ? t('provider.planBuilder.saving') : t('provider.planBuilder.save')}
          </Button>
        </div>
      </div>

      {error && (
        <div
          data-testid="plan-builder-error"
          className="px-3 py-2 mt-2 bg-rose-50 border border-rose-200 text-rose-700 text-[12px] rounded"
        >
          {error}
        </div>
      )}

      <div className="flex gap-0 mt-3">
        {/* Left rail — Journey Steps */}
        <aside
          data-testid="journey-step-rail"
          className="w-56 shrink-0 border-r border-slate-200 pr-3"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            {t('provider.planBuilder.journeyStepsHeading')}
          </div>
          <div className="space-y-1">
            {sortedSteps.length === 0 ? (
              <div className="text-[11px] text-slate-400" data-testid="journey-step-empty">
                {t('provider.planBuilder.journeyEmpty')}
              </div>
            ) : (
              sortedSteps.map((s, i) => (
                <div
                  key={s.id}
                  data-testid={`journey-step-${s.id}`}
                  className={`group flex items-center gap-1 rounded px-2 py-1 hover:bg-slate-50 ${
                    s.archived ? 'opacity-50' : ''
                  }`}
                >
                  <span
                    className="w-5 h-5 rounded bg-slate-900 text-white text-[10px] font-semibold flex items-center justify-center shrink-0"
                    data-testid={`journey-step-${s.id}-order`}
                  >
                    {i + 1}
                  </span>
                  {renamingStepId === s.id ? (
                    <input
                      autoFocus
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onBlur={() => renameJourneyStep(s.id, renameDraft)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameJourneyStep(s.id, renameDraft);
                        if (e.key === 'Escape') {
                          setRenamingStepId(null);
                          setRenameDraft('');
                        }
                      }}
                      data-testid={`journey-step-${s.id}-rename-input`}
                      className="flex-1 text-[12px] px-1 py-0.5 border border-slate-300 rounded"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setStepFilterId(s.id)}
                      data-testid={`journey-step-${s.id}-name`}
                      className={`flex-1 text-left text-[12px] font-medium truncate ${
                        stepFilterId === s.id
                          ? 'text-slate-900'
                          : 'text-slate-700'
                      }`}
                    >
                      {s.name}
                    </button>
                  )}
                  <button
                    type="button"
                    title={t('provider.planBuilder.renameTitle')}
                    onClick={() => {
                      setRenamingStepId(s.id);
                      setRenameDraft(s.name);
                    }}
                    data-testid={`journey-step-${s.id}-rename-btn`}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    title={t('provider.planBuilder.moveUpTitle')}
                    onClick={() => moveJourneyStep(s.id, 'up')}
                    disabled={i === 0}
                    data-testid={`journey-step-${s.id}-up`}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700 disabled:opacity-25"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    title={t('provider.planBuilder.moveDownTitle')}
                    onClick={() => moveJourneyStep(s.id, 'down')}
                    disabled={i === sortedSteps.length - 1}
                    data-testid={`journey-step-${s.id}-down`}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700 disabled:opacity-25"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    title={s.archived ? t('provider.planBuilder.unarchiveTitle') : t('provider.planBuilder.archiveTitle')}
                    onClick={() => archiveJourneyStep(s.id)}
                    data-testid={`journey-step-${s.id}-archive`}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600"
                  >
                    <Archive className="h-3 w-3" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Add Journey Step */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1">
              <Input
                value={newStepDraft}
                onChange={(e) => setNewStepDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addJourneyStep(newStepDraft);
                }}
                placeholder={t('provider.planBuilder.newStepPh')}
                data-testid="journey-step-add-input"
                className="h-7 text-[11px]"
              />
              <Button
                size="sm"
                onClick={() => addJourneyStep(newStepDraft)}
                disabled={!newStepDraft.trim()}
                data-testid="journey-step-add-btn"
                className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800 text-white px-2"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Quick row-add per filter */}
          {stepFilterId !== 'all' && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                {t('provider.planBuilder.pickerHeading')} · <span className="text-slate-700">{stepMap[stepFilterId]?.name}</span>
              </div>
              <Button
                size="sm"
                onClick={() => addRow('requirement', stepFilterId)}
                data-testid="plan-builder-add-requirement"
                className="w-full h-7 text-[11px] justify-start bg-sky-50 hover:bg-sky-100 text-sky-800"
              >
                <Plus className="h-3 w-3 mr-1" /> {t('provider.reqCatalog.chipDocument', { count: '' }).replace(/\s*$/, '').split(' ')[0] || 'Requirement'}
              </Button>
              <Button
                size="sm"
                onClick={() => addRow('task', stepFilterId)}
                data-testid="plan-builder-add-task"
                className="w-full h-7 text-[11px] justify-start bg-violet-50 hover:bg-violet-100 text-violet-800"
              >
                <Plus className="h-3 w-3 mr-1" /> {t('provider.planBuilder.btnAddTask')}
              </Button>
            </div>
          )}
        </aside>

        {/* Center — Grid + filter chips */}
        <div className="flex-1 min-w-0 pl-3">
          <FilterChips
            testIdPrefix="plan-builder-filter"
            activeId={stepFilterId}
            onChange={(id) => {
              setStepFilterId(id);
              setActiveRowId(null);
            }}
            chips={filterChips}
            resultLabel={t('provider.reqCatalog.chipAll', { count: filteredRows.length })}
          />
          <DataGrid
            testId="plan-builder-grid"
            columns={columns}
            rows={filteredRows}
            rowKey={(row) => row.id}
            selectable={false}
            onRowClick={(row) => setActiveRowId(row.id)}
            activeRowId={activeRowId}
            maxHeightClass="h-[420px]"
            emptyMessage={
              <span data-testid="plan-builder-empty">
                {sortedSteps.length === 0
                  ? t('provider.planBuilder.journeyEmpty')
                  : t('provider.planBuilder.assignedEmpty')}
              </span>
            }
          />
        </div>

        {/* Right — Detail drawer */}
        {activeRow && (
          <DetailDrawer
            testId="plan-builder-drawer"
            open={!!activeRow}
            onClose={() => setActiveRowId(null)}
            eyebrow={`${KIND_LABEL[activeRow.kind]} · ${stepMap[activeRow.step_id]?.name || '—'}`} /* backend kind label kept */
            title={activeRow.name || t('provider.planBuilder.reqTitleFallback')}
            widthClass="w-80"
            footer={
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    updateRow(activeRow.id, {
                      status:
                        activeRow.status === 'active' ? 'draft' : 'active',
                    });
                  }}
                  data-testid="plan-builder-drawer-toggle-active"
                  className="h-7 text-[11px] flex-1"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  {activeRow.status === 'active' ? t('provider.reqCatalog.btnCancel') : t('provider.reqCatalog.btnCreate')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteRow(activeRow.id)}
                  data-testid="plan-builder-drawer-delete"
                  className="h-7 text-[11px] text-rose-600 hover:bg-rose-50"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            }
          >
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {t('provider.reqCatalog.labelTitle')}
              </Label>
              <Input
                value={activeRow.name}
                onChange={(e) => updateRow(activeRow.id, { name: e.target.value })}
                data-testid="plan-builder-drawer-name"
                className="mt-1 text-[12px] h-8"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {t('provider.planBuilder.reqOwnerPatient')}
              </Label>
              <Input
                value={activeRow.patient_action}
                onChange={(e) =>
                  updateRow(activeRow.id, { patient_action: e.target.value })
                }
                placeholder={t('provider.planBuilder.pickerSearchPh')}
                data-testid="plan-builder-drawer-patient-action"
                className="mt-1 text-[12px] h-8"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {t('provider.planBuilder.reqOwnerLabel')}
              </Label>
              <select
                value={activeRow.default_role || ''}
                onChange={(e) =>
                  updateRow(activeRow.id, { default_role: e.target.value })
                }
                data-testid="plan-builder-drawer-default-role"
                className="mt-1 w-full text-[12px] h-8 border border-slate-300 rounded px-2"
              >
                <option value="">—</option>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {t('provider.planBuilder.reqOwnerClinic')}
              </Label>
              <Input
                value={activeRow.default_assignee || ''}
                onChange={(e) =>
                  updateRow(activeRow.id, { default_assignee: e.target.value })
                }
                placeholder={t('provider.planBuilder.reqInternalNotePh')}
                data-testid="plan-builder-drawer-default-assignee"
                className="mt-1 text-[12px] h-8"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <label
                className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                data-testid="plan-builder-drawer-doc-required-label"
              >
                <input
                  type="checkbox"
                  checked={!!activeRow.document_required}
                  onChange={(e) =>
                    updateRow(activeRow.id, {
                      document_required: e.target.checked,
                    })
                  }
                  data-testid="plan-builder-drawer-doc-required"
                />
                {t('provider.reqCatalog.typeDocument')}
              </label>
              <label
                className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                data-testid="plan-builder-drawer-consent-required-label"
              >
                <input
                  type="checkbox"
                  checked={!!activeRow.consent_required}
                  onChange={(e) =>
                    updateRow(activeRow.id, {
                      consent_required: e.target.checked,
                    })
                  }
                  data-testid="plan-builder-drawer-consent-required"
                />
                {t('provider.reqCatalog.typeConsent')}
              </label>
            </div>

            {/* REQUIREMENT-FOUNDATION-001 Phase 4 — canonical Requirement Catalog binding. */}
            <div data-testid="plan-builder-drawer-requirement-binding">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {t('provider.reqCatalog.pageTitle')}
              </Label>
              <select
                value={activeRow.requirement_id || ''}
                onChange={(e) => {
                  const rid = e.target.value || null;
                  const selectedReq = reqCatalog.find((r) => r.requirement_id === rid);
                  updateRow(activeRow.id, {
                    requirement_id: rid,
                    // Mirror the canonical title into the row for offline display;
                    // materialization will freeze the authoritative version anyway.
                    name: selectedReq ? selectedReq.title : activeRow.name,
                    kind: rid ? 'requirement' : (activeRow.kind || 'task'),
                  });
                }}
                className="mt-1 w-full text-[12px] h-8 border border-slate-300 rounded px-2"
                data-testid="plan-builder-drawer-requirement-select"
              >
                <option value="">{t('provider.planBuilder.reqTitleFallback')}</option>
                {reqCatalog.filter((r) => !r.archived).map((r) => (
                  <option key={r.requirement_id} value={r.requirement_id}>
                    [{r.type}] {r.title} (v{r.version})
                  </option>
                ))}
              </select>
              {activeRow.requirement_id ? (
                <p className="mt-1 text-[11px] text-slate-500" data-testid="plan-builder-bound-note">
                  <code>{activeRow.requirement_id}</code>
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-slate-400">
                  {t('provider.planBuilder.reqOffsetHelp')}
                </p>
              )}
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {t('provider.planBuilder.reqOffsetDaysLabel')}
              </Label>
              <Input
                type="number"
                min={-365}
                max={365}
                value={activeRow.due_offset_days ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateRow(activeRow.id, {
                    due_offset_days: v === '' ? null : Number(v),
                  });
                }}
                data-testid="plan-builder-drawer-due"
                className="mt-1 text-[12px] h-8"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {t('provider.planBuilder.reqInternalNoteLabel')}
              </Label>
              <textarea
                value={activeRow.notes || ''}
                onChange={(e) => updateRow(activeRow.id, { notes: e.target.value })}
                placeholder={t('provider.planBuilder.reqInternalNotePh')}
                data-testid="plan-builder-drawer-notes"
                className="mt-1 w-full text-[12px] border border-slate-300 rounded p-2 h-20"
              />
            </div>

            <DrawerField
              label={t('provider.reqCatalog.fieldType')}
              value={<Pill tone={STATUS_TONE[activeRow.status]}>{activeRow.status}</Pill>}
              testId="plan-builder-drawer-status"
            />
          </DetailDrawer>
        )}
      </div>
    </div>
  );
}
